import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { panelResolver } from 'app/core/panel/panel.resolver';
import { LayoutComponent } from 'app/layout/layout.component';
import { AdminGuard } from './core/auth/guards/admin.guard';
import { UserGuard } from './core/auth/guards/user.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

export const appRoutes: Route[] = [
    // Po zalogowaniu
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: ''},
    {path: 'admin', pathMatch : 'full', redirectTo: 'admin/dashboard'},

    // Dawne adresy panelu → trasy z frontend-spec.md
    {path: 'dashboard', pathMatch : 'full', redirectTo: ''},
    {path: 'vehicles', redirectTo: 'pojazdy'},
    {path: 'policies', redirectTo: 'polisy'},
    {path: 'finances', redirectTo: 'finansowanie'},

    // Panel klienta (powłoka VAZUVI) — trasy wg docs/design/frontend-spec.md.
    // Przed grupami logowania: grupa z path '' i dziećmi dopasowuje też pusty adres „/”,
    // a NoAuthGuard odsyła zalogowanego na „/” — kolejność chroni przed pętlą przekierowań.
    {
        path: '',
        canActivate: [AuthGuard, UserGuard],
        canActivateChild: [AuthGuard, UserGuard],
        component: LayoutComponent,
        data: {
            layout: 'vazuvi'
        },
        resolve: {
            panel: panelResolver
        },
        children: [
            {path: '', pathMatch: 'full', title: 'Strona główna · VAZUVI', loadComponent: () => import('app/modules/home/home.component').then((m) => m.HomeComponent)},
            {path: 'sprawy', title: 'Sprawy · VAZUVI', loadComponent: () => import('app/modules/cases/cases.component').then((m) => m.CasesComponent)},
            // Zgłoszenia (Issue 40): część listy „Sprawy”, nowe — z „Załatw sprawę” i ze stron pozycji
            {path: 'zgloszenia/nowe', title: 'Załatw sprawę · VAZUVI', loadComponent: () => import('app/modules/tickets/new-ticket.component').then((m) => m.NewTicketComponent)},
            {path: 'zgloszenia/:id', title: 'Zgłoszenie · VAZUVI', loadComponent: () => import('app/modules/tickets/ticket.component').then((m) => m.TicketComponent)},
            {path: 'sprawy/:id', title: 'Sprawa · VAZUVI', loadComponent: () => import('app/modules/cases/case.component').then((m) => m.CaseComponent)},
            {path: 'pojazdy', title: 'Pojazdy · VAZUVI', loadComponent: () => import('app/modules/vehicles/vehicles.component').then((m) => m.VehiclesComponent)},
            {path: 'pojazdy/:id', title: 'Pojazd · VAZUVI', loadComponent: () => import('app/modules/vehicles/vehicle.component').then((m) => m.VehicleComponent)},
            {path: 'polisy', title: 'Polisy · VAZUVI', loadComponent: () => import('app/modules/policies/policies.component').then((m) => m.PoliciesComponent)},
            {path: 'polisy/:id', title: 'Polisa · VAZUVI', loadComponent: () => import('app/modules/policies/policy.component').then((m) => m.PolicyComponent)},
            {path: 'finansowanie', title: 'Finansowanie · VAZUVI', loadComponent: () => import('app/modules/financing/financing.component').then((m) => m.FinancingComponent)},
            {path: 'finansowanie/:id', title: 'Umowa · VAZUVI', loadComponent: () => import('app/modules/financing/contract.component').then((m) => m.ContractComponent)},
            {path: 'dokumenty', title: 'Dokumenty · VAZUVI', loadComponent: () => import('app/modules/documents/documents.component').then((m) => m.DocumentsComponent)},
            {path: 'konto', title: 'Dane konta · VAZUVI', loadComponent: () => import('app/modules/account/account.component').then((m) => m.AccountComponent)},
        ]
    },
    // Logowanie, dostęp i hasło (Issue 41, 42) — ekrany VAZUVI zamiast szablonu Fuse; bez rejestracji (D31)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'logowanie', canActivate: [NoAuthGuard], title: 'Logowanie · VAZUVI', loadComponent: () => import('app/modules/auth/login.component').then((m) => m.LoginComponent)},
            {path: 'dostep', canActivate: [NoAuthGuard], title: 'Zapytaj o dostęp · VAZUVI', loadComponent: () => import('app/modules/auth/access-request.component').then((m) => m.AccessRequestComponent)},
            {path: 'oferta', canActivate: [NoAuthGuard], title: 'Zapytaj o ofertę · VAZUVI', data: {kind: 'offer'}, loadComponent: () => import('app/modules/auth/access-request.component').then((m) => m.AccessRequestComponent)},
            {path: 'nie-pamietam-hasla', canActivate: [NoAuthGuard], title: 'Nie pamiętasz hasła? · VAZUVI', loadComponent: () => import('app/modules/auth/forgot-password.component').then((m) => m.ForgotPasswordComponent)},
            // Bez strażnika: link z maila otwarty przy zalogowanym koncie ustawia hasło konta z linku
            {path: 'ustaw-haslo', title: 'Ustaw hasło · VAZUVI', loadComponent: () => import('app/modules/auth/set-password.component').then((m) => m.SetPasswordComponent)},
            // Bez strażnika: wejście z sesją (menu „Wyloguj”) najpierw ją kończy
            {path: 'wylogowano', title: 'Wylogowano · VAZUVI', loadComponent: () => import('app/modules/auth/logged-out.component').then((m) => m.LoggedOutComponent)},
        ]
    },

    // Dawne adresy szablonu Fuse; linki z maili sprzed rundy 2 zachowują ?token=
    {path: 'sign-in', redirectTo: 'logowanie'},
    {path: 'sign-up', redirectTo: 'dostep'},
    {path: 'sign-out', redirectTo: 'wylogowano'},
    {path: 'forgot-password', redirectTo: 'nie-pamietam-hasla'},
    {path: 'reset-password', redirectTo: 'ustaw-haslo'},
    {path: 'user-verification', redirectTo: 'ustaw-haslo'},
    {path: 'unlock-session', redirectTo: 'logowanie'},
    {path: 'confirmation-required', redirectTo: 'logowanie'},

    // Admin routes
    {
        path: 'admin',
        canActivate: [AuthGuard, AdminGuard],
        canActivateChild: [AuthGuard, AdminGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: '', loadChildren: () => import('app/modules/admin/admin.routes')},
        ]
    },

    // Nieznany adres → 404 w powłoce panelu (Issue 6 p. 2); musi być ostatnia
    {
        path: '',
        canActivate: [AuthGuard, UserGuard],
        canActivateChild: [AuthGuard, UserGuard],
        component: LayoutComponent,
        data: {
            layout: 'vazuvi'
        },
        resolve: {
            panel: panelResolver
        },
        children: [
            {path: '**', title: 'Nie ma takiej strony · VAZUVI', loadComponent: () => import('app/modules/not-found/not-found.component').then((m) => m.NotFoundComponent)},
        ]
    },
];
